// Layer 4 (UI) — the Value lost tab (a20-maintenance S4, rebuilt to the
// locked comp `downtime.html` A). Renders the window's unplanned stoppages
// priced at the constraint; every number on the glass comes from
// ValueLost.transform.ts over the event log, nothing is typed here.
//
// The cumulative curve is `TrendChart kind="line"` on a calendar axis (one
// point per day boundary, flat between events, so each event is a step at its
// date), with an area, no per-point markers, and the two costliest events
// marked on the top of their steps without guides; the y domain follows this
// window's US$K values from a zero floor and reserves the `clear` labels' own
// ink height plus gap above the maximum, so the top mark's label sits above
// its step rather than below the plateau.
//
// The one policy number below is a view decision, not seed: how many event
// rows the preview carries before the ghost button.

import { useState } from 'react';
import { Button, Table, TBody, TD, TH, THead, TR } from '@/design-system';
import { KpiCard, TrendChart, markerLabelHeadroomFraction } from '@/versions/v1/modules/_viz';
import { formatCurrency, formatCurrencyCompact } from '@/versions/v1/modules/capacity/Scene.transform';
import { Commentable } from '@/shared/comments/Commentable';
import { formatDayMonth } from '@/versions/v1/shared/clock.derive';
import { KpiStrip } from '../maintenance/KpiStrip';
import { causeCategoryLabel, type DowntimeEvent } from '../maintenance/DowntimeEvents.transform';
import type { CauseCategoryId } from '../maintenance/downtime.seed';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import { formatHours } from '../maintenance/Downtime.transform';
import { CARD_TABLE_DENSE, DowntimeCard, HOT_ROW, RankBar, SYN_CHIP, THREE_UP } from './DowntimeCard';
import {
  cumulativeValueLostMarkerLabel,
  cumulativeValueLostMarkers,
  cumulativeValueLostOnGrid,
  cumulativeValueLostYDomain,
  valueLostCategoriesForCause,
  valueLostEventRows,
  valueLostKpis,
  valueLostRowsForCause,
} from './ValueLost.transform';
import { CURRENCY_GLYPH } from '@/versions/v1/shared/money';

/** Six rows is the height budget the comp draws; the footer always sums all
 *  rows, expanded or not. */
const EVENT_TABLE_PREVIEW_ROWS = 6;

/** The cumulative chart's drawn height: the domain's label headroom is the
 *  `clear` placement's ink height plus gap as a fraction of this plot. */
const CUMULATIVE_CHART_HEIGHT = 200;

export function ValueLostTab({
  rows,
  windowLabel,
  windowFrom,
  windowTo,
  selectedCause,
}: {
  rows: readonly DowntimeEvent[];
  windowLabel: string;
  /** The selected window's bounds: the cumulative curve runs one point per
   *  day boundary between them. */
  windowFrom: SeriesTime;
  windowTo: SeriesTime;
  selectedCause?: CauseCategoryId;
}) {
  const selectedRows = valueLostRowsForCause(rows, selectedCause);
  const selectedCauseLabel = selectedCause ? causeCategoryLabel(selectedCause) : undefined;
  const kpis = valueLostKpis(selectedRows, windowLabel);
  const cumulative = cumulativeValueLostOnGrid(selectedRows, windowFrom, windowTo);
  const markers = cumulativeValueLostMarkers(selectedRows, windowFrom, windowTo);
  const visibleCategories = valueLostCategoriesForCause(rows, selectedCause);
  const events = valueLostEventRows(selectedRows);
  const [expanded, setExpanded] = useState(false);

  const topCategoryDollars = visibleCategories[0]?.dollars ?? 0;
  const shown = expanded ? events : events.slice(0, EVENT_TABLE_PREVIEW_ROWS);
  const yDomain = cumulativeValueLostYDomain(cumulative.points, markerLabelHeadroomFraction(CUMULATIVE_CHART_HEIGHT));

  return (
    <Commentable name="ValueLost">
      <div className="space-y-3">
        <div className={THREE_UP}>
          <KpiStrip>
            <KpiCard
              label={selectedCauseLabel ? 'Value lost · cause' : 'Value lost · selected window'}
              value={formatCurrency(kpis.total)}
              sub={`${selectedCauseLabel ? `${selectedCauseLabel} · ` : ''}${kpis.eventCount} events · ${formatCurrency(kpis.ratePerHour)}/lost mill hour`}
              tone="bad"
              className="px-3.5 py-3"
            />
            <KpiCard
              label="Largest single event"
              value={formatCurrencyCompact(kpis.largest.dollars)}
              sub={`${kpis.largest.itemId} · ${formatHours(kpis.largest.hours)} · ${formatDayMonth(kpis.largest.start)}`}
              tone="bad"
              className="px-3.5 py-3"
            />
            <KpiCard
              label="Mean per event"
              value={formatCurrencyCompact(kpis.averagePerEvent)}
              sub={`${kpis.eventCount} unplanned events`}
              tone="neutral"
              className="px-3.5 py-3"
            />
          </KpiStrip>
        </div>

        <DowntimeCard
          title="Cumulative value lost"
          meta={`${CURRENCY_GLYPH} thousands, by event · ${kpis.windowLabel}`}
          actions={SYN_CHIP}
        >
          <TrendChart
            kind="line"
            categories={cumulative.categories}
            series={[{ name: 'Value lost', points: cumulative.points, tone: 'bad', area: true }]}
            markers={markers.map((marker) => ({
              index: marker.index,
              value: marker.valueK,
              label: cumulativeValueLostMarkerLabel(marker),
              tone: 'bad',
            }))}
            markerTitleFormatter={(marker) => marker.label}
            pointMarkers={false}
            markerGuides={false}
            markerLabelPlacement="clear"
            markerLabelHalo={0}
            dedupeTickLabels
            avoidTickOverlap
            height={CUMULATIVE_CHART_HEIGHT}
            yDomain={yDomain}
          />
        </DowntimeCard>

        <div className="grid min-w-0 gap-3 xl:grid-cols-12 xl:items-start">
          <DowntimeCard title="Value lost by cause" actions={SYN_CHIP} className="xl:col-span-5">
            <div className="space-y-2">
              {visibleCategories.map((row, index) => (
                <div key={row.causeCategory} className="grid grid-cols-[11rem_minmax(0,1fr)_7rem] items-center gap-3 text-sm tabular-nums">
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-ink-primary">{row.label}</span>
                    <span className="block text-xs text-muted">{row.eventCount} ev · {formatHours(row.hours)}</span>
                  </span>
                  <RankBar ratio={topCategoryDollars > 0 ? row.dollars / topCategoryDollars : 0} tone={index === 0 ? 'top' : 'ink'} />
                  <span className="text-right">
                    <span className="block font-semibold text-ink-primary">{formatCurrency(row.dollars)}</span>
                    <span className="block text-xs text-muted">{Math.round(row.sharePct)}% of window</span>
                  </span>
                </div>
              ))}
            </div>
          </DowntimeCard>

          <DowntimeCard
            title="Unplanned events, worst first"
            meta={kpis.windowLabel}
            className="xl:col-span-7"
            actions={
              <>
                {SYN_CHIP}
                <Button
                  variant="ghost"
                  className="h-6 border border-[var(--hairline-strong)] px-2 text-xs"
                  onClick={() => setExpanded((open) => !open)}
                >
                  {expanded ? 'Show fewer' : `Show all ${events.length} events`}
                </Button>
              </>
            }
          >
            <div className={CARD_TABLE_DENSE}>
              <Table wrap={false} className="min-w-[560px] tabular-nums">
                <THead>
                  <TR>
                    <TH>Date</TH>
                    <TH>Item</TH>
                    <TH>Cause</TH>
                    <TH className="text-right">Hours</TH>
                    <TH className="text-right">Value lost</TH>
                  </TR>
                </THead>
                <TBody>
                  {shown.map((row, index) => (
                    <TR key={row.id} className={index === 0 ? HOT_ROW : undefined}>
                      <TD className="whitespace-nowrap">
                        {formatDayMonth(row.start)} · {row.start.slice(11, 16)}
                      </TD>
                      <TD className="whitespace-nowrap tracking-[0.04em] !text-ink-primary">{row.itemId}</TD>
                      <TD>
                        <span className="block font-medium text-ink-primary">{row.subCause}</span>
                        <span className="block text-xs text-muted">{row.causeLabel}</span>
                      </TD>
                      <TD className="whitespace-nowrap text-right">{formatHours(row.hours)}</TD>
                      <TD className="whitespace-nowrap text-right font-semibold !text-ink-primary">{formatCurrency(row.dollars)}</TD>
                    </TR>
                  ))}
                  <TR className="border-t border-[var(--hairline-strong)] hover:bg-transparent dark:hover:bg-transparent">
                    <TD className="whitespace-nowrap font-semibold !text-ink-primary" colSpan={4}>Total · {selectedCauseLabel ?? kpis.windowLabel}</TD>
                    <TD className="whitespace-nowrap text-right font-semibold !text-ink-primary">{formatCurrency(kpis.total)}</TD>
                  </TR>
                </TBody>
              </Table>
            </div>
          </DowntimeCard>
        </div>
      </div>
    </Commentable>
  );
}
